import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Popconfirm, Space, Table, Tree, message } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { adminApi, type PageResult } from '../../api'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'

type Role = { id: number; code: string; name: string; description: string; menu_ids: number[] }
type Menu = {
  id: number
  parent_id: number | null
  name: string
  sort_order: number
}

function buildMenuTree(menus: Menu[]): DataNode[] {
  const sorted = [...menus].sort((a, b) => a.sort_order - b.sort_order)
  const childrenMap = new Map<number, Menu[]>()
  for (const m of sorted) {
    if (m.parent_id != null) {
      const list = childrenMap.get(m.parent_id) || []
      list.push(m)
      childrenMap.set(m.parent_id, list)
    }
  }
  const roots = sorted.filter((m) => m.parent_id == null)

  const toNode = (m: Menu): DataNode => ({
    key: m.id,
    title: m.name,
    children: (childrenMap.get(m.id) || [])
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(toNode),
  })

  return roots.map(toNode)
}

function expandMenuIdsWithParents(ids: number[], menus: Menu[]): number[] {
  const byId = new Map(menus.map((m) => [m.id, m]))
  const out = new Set(ids)
  for (const id of ids) {
    let cur = byId.get(id)
    while (cur?.parent_id) {
      out.add(cur.parent_id)
      cur = byId.get(cur.parent_id)
    }
  }
  return [...out]
}

function MenuPermissionTree({
  value,
  onChange,
  treeData,
}: {
  value?: number[]
  onChange?: (v: number[]) => void
  treeData: DataNode[]
}) {
  return (
    <Tree
      checkable
      defaultExpandAll
      treeData={treeData}
      checkedKeys={value || []}
      onCheck={(keys) => {
        const checked = Array.isArray(keys) ? keys : keys.checked
        onChange?.(checked.map((k) => Number(k)))
      }}
      style={{
        maxHeight: 360,
        overflow: 'auto',
        border: '1px solid #d9d9d9',
        borderRadius: 6,
        padding: 8,
      }}
    />
  )
}

export default function Roles() {
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [rows, setRows] = useState<Role[]>([])
  const [total, setTotal] = useState(0)
  const [menus, setMenus] = useState<Menu[]>([])
  const [open, setOpen] = useState(false)
  const [ed, setEd] = useState<Role | null>(null)
  const [form] = Form.useForm()

  const menuTree = useMemo(() => buildMenuTree(menus), [menus])

  const loadRoles = useCallback((p: number, ps: number) => {
    adminApi
      .get<PageResult<Role>>('/admin/roles', { params: { page: p, page_size: ps } })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
  }, [])

  useEffect(() => {
    loadRoles(page, pageSize)
  }, [page, pageSize, loadRoles])

  useEffect(() => {
    adminApi
      .get<PageResult<Menu>>('/admin/menus', { params: { page: 1, page_size: 100 } })
      .then((r) => setMenus(r.data.items))
      .catch((e) => message.error(e.message))
  }, [])

  const submit = () => {
    void form.validateFields().then((v) => {
      const body = {
        ...v,
        menu_ids: expandMenuIdsWithParents(v.menu_ids || [], menus),
      }
      const req = ed ? adminApi.put(`/admin/roles/${ed.id}`, body) : adminApi.post('/admin/roles', body)
      req
        .then(() => {
          message.success('已保存')
          setOpen(false)
          loadRoles(page, pageSize)
        })
        .catch((e) => message.error(e.message))
    })
  }

  const remove = (role: Role) => {
    adminApi
      .delete(`/admin/roles/${role.id}`)
      .then(() => {
        message.success('已删除')
        loadRoles(page, pageSize)
      })
      .catch((e) => message.error(e.message))
  }

  return (
    <div>
      <PageHeader title="角色管理" />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          type="primary"
          onClick={() => {
            setEd(null)
            form.resetFields()
            form.setFieldsValue({ menu_ids: [] })
            setOpen(true)
          }}
        >
          新增角色
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '说明', dataIndex: 'description', ellipsis: true },
          {
            title: '操作',
            render: (_, r) => (
              <Space size="small">
                <Button
                  size="small"
                  onClick={() => {
                    setEd(r)
                    form.setFieldsValue(r)
                    setOpen(true)
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title={`确定删除角色「${r.name}」？`}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(r)}
                >
                  <Button size="small" danger disabled={r.code === 'admin'}>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={ed ? '编辑角色' : '新增角色'}
        open={open}
        onOk={submit}
        onCancel={() => setOpen(false)}
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input />
          </Form.Item>
          <Form.Item name="menu_ids" label="菜单权限">
            <MenuPermissionTree treeData={menuTree} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
